import { useState, useEffect, useRef } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import FilterModal from '../FilterModal';
import { FacetCount } from 'utils/wemble-api.generated';

interface TitleModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (titles: string[]) => void;
  options: FacetCount[];
  initialSelected: string[];
}

const TitleModal = ({ visible, onClose, onSave, options, initialSelected }: TitleModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (visible) {
      setSelected(initialSelected);
      setSearch('');
    }
  }, [visible]);

  const toggle = (name: string) => {
    setSelected(prev =>
      prev.includes(name) ? prev.filter(t => t !== name) : [...prev, name]
    );
  };

  // Sort order is fixed when modal opens — selected first, then by count. No jumping around.
  const sortOrderRef = useRef<string[]>([]);
  useEffect(() => {
    if (visible) {
      sortOrderRef.current = [...options]
        .sort((a, b) => {
          const aSelected = initialSelected.includes(a.name);
          const bSelected = initialSelected.includes(b.name);
          if (aSelected && !bSelected) return -1;
          if (!aSelected && bSelected) return 1;
          return b.count - a.count;
        })
        .map(o => o.name);
    }
  }, [visible]);

  const filteredOptions = options
    .filter(o => selected.includes(o.name) || !search || o.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => sortOrderRef.current.indexOf(a.name) - sortOrderRef.current.indexOf(b.name));

  const handleSave = () => {
    onSave(selected);
    onClose();
  };

  return (
    <FilterModal
      visible={visible}
      onClose={onClose}
      onSave={handleSave}
      title="Title"
      description="Filter by job title"
    >
      <div>
        {options.length > 5 && (
          <input
            autoFocus={!isMobile}
            type="text"
            placeholder="Search titles..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #e0e0e0',
              fontSize: 13,
              marginBottom: 8,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, minHeight: 18 }}>
          {selected.length > 0 ? (
            <>
              <span style={{ fontSize: 12, color: '#999' }}>
                {selected.length} selected
              </span>
              <button
                onClick={() => setSelected([])}
                style={{ fontSize: 12, color: '#4A9BBF', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Clear all
              </button>
            </>
          ) : (
            <span style={{ fontSize: 12, color: '#ccc' }}>No titles selected</span>
          )}
        </div>

        {filteredOptions.map(option => {
          const isSelected = selected.includes(option.name);
          return (
            <div
              key={option.name}
              onClick={() => toggle(option.name)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 12px',
                borderRadius: 6,
                cursor: 'pointer',
                backgroundColor: isSelected ? '#F0F7FA' : 'transparent',
                userSelect: 'none', WebkitUserSelect: 'none' as any,
                transition: 'background-color 0.1s',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#FAFAFA'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 18, height: 18, borderRadius: 4,
                  border: isSelected ? 'none' : '2px solid #ccc',
                  backgroundColor: isSelected ? '#4A9BBF' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, color: 'white', fontWeight: 700, flexShrink: 0,
                }}>
                  {isSelected && '✓'}
                </div>
                <span style={{ fontSize: 14, color: '#333' }}>{option.name || '(no title)'}</span>
              </div>
              <span style={{ fontSize: 12, color: '#999' }}>{option.count}</span>
            </div>
          );
        })}

        {filteredOptions.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>
            {search ? 'No titles match your search' : 'No titles available'}
          </div>
        )}
      </div>
    </FilterModal>
  );
};

export default TitleModal;
